"use client";

import { createContext, useContext, ReactNode, useRef } from 'react';
import { SessionManager } from '@/utils/sessionManager';

interface SessionContextType {
  sessionId: string;
  getTraceId: () => string | null;
  createNewTraceId: (prefix?: string) => string;
  isMessageProcessed: (message: string) => boolean;
  setLastProcessedMessage: (message: string) => void;
  reset: () => void;
}

const SessionContext = createContext<SessionContextType | null>(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
  sessionId?: string; // Optional: provide existing session ID
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ 
  children, 
  sessionId 
}) => {
  // Create session manager instance that persists for component lifetime
  const sessionManagerRef = useRef(new SessionManager(sessionId));
  const sessionManager = sessionManagerRef.current;
  
  const value: SessionContextType = {
    sessionId: sessionManager.getSessionId(),
    getTraceId: () => sessionManager.getTraceId(),
    createNewTraceId: (prefix) => sessionManager.createNewTraceId(prefix),
    isMessageProcessed: (message) => sessionManager.isMessageProcessed(message),
    setLastProcessedMessage: (message) => sessionManager.setLastProcessedMessage(message),
    reset: () => sessionManager.reset()
  };
  
  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};